import React, {useEffect, useState} from "react"
import PropTypes from 'prop-types'
import SkeletonChart from "src/v2/components/Chart/SkeletonChart"
import {alpha, Box} from "@mui/material"
import {generateSingleColor} from "src/utils/colors"
import HighchartsReact from "highcharts-react-official"
import Highcharts from 'src/utils/highchart-configure'

export const CHART_TYPE_LINE = "line"
export const CHART_TYPE_SCATTER = "scatter"
export const CHART_TYPE_BOX_PLOT = "boxplot"
// export const CHART_TYPE_BOX_PLOT_SCATTER = "boxplot_scatter"

const MarketToMarketHighChart = ({xValue, data, loading, labelX, labelY, maxX}) => {
    const [layoutData, setLayoutData] = useState(null)

    useEffect(() => {
        loadPlotData()
    }, [data, xValue, labelX, labelY, maxX])

    const getLinhaGraficoHighcharts = (item) => {
        const {data, label, tooltip, color, text} = item
        const {title, x, y} = tooltip

        return {
            type: 'spline',
            name: label,
            data: data.map((value, i) => ({
                x: xValue[i],
                y: value,
                marker: {
                    enabled: i === xValue.length - 1 && !!text,
                    symbol: 'circle',
                },
                dataLabels: text && i === xValue.length - 1 ? {
                    enabled: true,
                    format: `<b>${text}</b>`,
                    align: 'left',
                    x: 4,
                    verticalAlign: 'middle',
                    style: {
                        textOutline: 'none',
                        color: color || generateSingleColor(),
                        fontWeight: 'bold',
                        fontSize: '14px',
                    }
                } : {enabled: false}
            })),
            color: color || generateSingleColor(),
            lineWidth: 2,
            plotOptions: {
                spline: {
                    smoothing: 0
                }
            },
            dashStyle: 'Solid',
            tooltip: {
                useHTML: true,
                headerFormat: ``,
                pointFormat:
                    `${title || ''} : ${label} <br>` +
                    `${x || 'X'} : {point.x} <br>` +
                    `${y || 'Y'} : {point.y:.4f} <br>`,
                valueDecimals: 4,
            },
            marker: {
                enabled: false
            }
        }
    }

    const geraScatterSeries = (item) => {
        const color = item.color || generateSingleColor()
        return {
            type: 'scatter',
            name: item.label,
            data: item.data.map(d => [d.x, d.y]),
            color: alpha(color, 0.2),
            marker: {
                fillColor: alpha(color, 0.2),
                radius: 8,
                symbol: 'triangle',
                lineColor: alpha(color, 0.5),
                lineWidth: 1
            },
            states: {
                hover: {
                    enabled: true
                }
            },
            zIndex: 2,
            tooltip: {
                pointFormat: item.tooltip || ''
            }
        }
    }

    const getBoxplotHighchartsSeries = (item) => {
        const {label, data, x, tooltip} = item

        const color = item.color || generateSingleColor(label)
        const {Min, Max, Q1, Q3, Median} = data
        const xIndex = Number(x)

        return {
            type: 'boxplot',
            grouping: false,
            name: label,
            data: [
                {
                    x: xIndex,   // <-- aqui você posiciona no eixo X
                    low: Min,
                    q1: Q1,
                    median: Median,
                    q3: Q3,
                    high: Max
                }
            ],
            // pointPlacement: xIndex,
            color: color,
            fillColor: alpha(color, 0.2),
            lineWidth: 1,
            states: {
                hover: {
                    enabled: true
                }
            },
            zIndex: 3,
            tooltip: {
                useHTML: true,
                headerFormat: `<em></em><br/>`,
                pointFormat: tooltip
            },

        }
    }

    const loadPlotData = () => {
        const newPlot = data.flatMap(item => {
            switch (item.type) {
                case CHART_TYPE_LINE:
                    return getLinhaGraficoHighcharts(item)
                case CHART_TYPE_SCATTER:
                    return geraScatterSeries(item)
                case CHART_TYPE_BOX_PLOT:
                    return getBoxplotHighchartsSeries(item)
                default:
                    return []
            }
        }).filter(Boolean)

        loadLayoutData(newPlot)
    }

    const loadLayoutData = (plot) => {
        const fontAxis = {
            fontFamily: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
            fontSize: 12
        }

        const layout = {
            credits: {enabled: false},
            chart: {
                type: 'line',
                zoomType: 'xy',
                height: window.innerHeight * 0.6,
                spacing: [40, 32, 22, 22], // [top, right, bottom, left]
                zooming: {
                    mouseWheel: {
                        enabled: false
                    }
                }
            },
            title: {
                text: null,
            },
            xAxis: {
                title: {
                    text: labelX || '',
                    style: fontAxis
                },
                tickInterval: 0.5,
                gridLineWidth: 1,
                lineWidth: 1,
                softThreshold: true,
                lineColor: 'rgba(0,0,0,0.2)',
            },
            yAxis: {
                title: {
                    text: labelY || '',
                    style: fontAxis
                },
                startOnTick: true,
                endOnTick: true,
                softThreshold: true,
                gridLineWidth: 1,
                lineWidth: 1,
                lineColor: 'rgba(0,0,0,0.2)',
            },
            legend: {
                enabled: true
            },
            tooltip: {
                shared: false,
            },
            series: plot,
            exporting: {
                enabled: true,
                filename: 'chart',
                useMultiLevelHeaders: true,
                useRowspanHeaders: true,
                showExportInProgress: true,
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        menuItems: [
                            'downloadPNG',
                            'downloadJPEG',
                            'downloadPDF',
                            'downloadSVG',
                            'separator',
                            'downloadCSV',
                            'downloadXLS',
                            'viewFullscreen',
                            'printChart'
                        ]
                    }
                }
            },
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 500
                    },
                    chartOptions: {
                        legend: {
                            layout: 'horizontal',
                            align: 'center',
                            verticalAlign: 'bottom'
                        }
                    }
                }]
            }
        }
        setLayoutData(layout)
    }


    return (
        <>
            {loading ?
                <>
                    <br/>
                    <SkeletonChart/>
                </>
                :
                <Box sx={{height: '60vh', display: 'flex', flexDirection: 'column'}}>
                    <Box sx={{flexGrow: 1, position: 'relative'}}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={layoutData}
                            immutable={true}
                            containerProps={{style: {width: '100%', height: '100%'}}}
                        />
                    </Box>
                </Box>
            }
        </>
    )
}

MarketToMarketHighChart.propTypes = {
    xValue: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    labelX: PropTypes.string,
    labelY: PropTypes.string,
    maxX: PropTypes.number
}

export default MarketToMarketHighChart
